import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Calendar, Mail, Phone, MapPin, FileText, Download, CheckCircle2, ArrowRight, UserPlus, RefreshCw, Users, Info, AlertCircle, Clock, Target } from "lucide-react";
import PreInscriptionForm from "@/components/PreInscriptionForm";
import UpcomingDetections from "@/components/UpcomingDetections";

export default function Inscriptions() {
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    api.get("/documents").then((r) => {
      // Only "Santé" documents (certificat médical type) are shown.
      // The registration dossier itself is NOT downloadable — it's given at the trial session.
      setDocs(r.data.filter((d) => d.category === "Santé"));
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="inscriptions-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0F172A] text-white">
        <div className="absolute -top-20 -right-20 h-80 w-80 bg-[#D97706]/30 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -left-20 h-80 w-80 bg-[#1E3A8A]/40 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="grid lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-3">
              <span className="text-xs tracking-[0.3em] uppercase text-[#D97706] font-bold">Rejoignez le club</span>
              <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05]" style={{ fontFamily: "Outfit" }}>
                Inscriptions &<br />
                <span className="text-[#D97706]">renouvellements</span>.
              </h1>
              <p className="mt-5 text-lg text-slate-200 max-w-xl leading-relaxed">
                Nous accueillons les joueurs toute l'année ! Les inscriptions pour la saison 2027-2028 sont ouvertes du 2 mai au 30 août 2027.
              </p>
            </div>
            <div className="lg:col-span-2">
              <div className="rounded-lg border border-white/15 bg-white/5 backdrop-blur p-6" data-testid="inscriptions-banner">
                <div className="flex items-center gap-2 text-[#D97706] font-bold text-xs tracking-[0.2em] uppercase">
                  <Calendar size={14} /> Dates clés
                </div>
                <div className="mt-4 space-y-3 text-sm">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-[#D97706] shrink-0 mt-0.5" />
                    <div>
                      <div className="font-bold">Toute l'année</div>
                      <div className="text-slate-300">Inscriptions possibles en cours de saison</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-[#D97706] shrink-0 mt-0.5" />
                    <div>
                      <div className="font-bold">02/05/2027 → 30/08/2027</div>
                      <div className="text-slate-300">Fenêtre officielle saison 2027-2028</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <AlertCircle size={16} className="text-[#DC2626] shrink-0 mt-0.5" />
                    <div>
                      <div className="font-bold">Après le 15 juillet</div>
                      <div className="text-slate-300">Les frais de mutation peuvent augmenter</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROCHAINES DATES DE DÉTECTION */}
      <UpcomingDetections />

      {/* TARIFS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="mb-10">
          <span className="text-xs tracking-[0.3em] uppercase text-[#1E3A8A] font-bold">Combien ça coûte ?</span>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: "Outfit" }}>
            Tarifs des licences
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-5" data-testid="tarifs-grid">
          <div className="relative rounded-lg border-2 border-[#1E3A8A] bg-white p-6 lg:p-8" data-testid="tarif-renouvellement">
            <span className="absolute -top-3 left-6 px-2.5 py-1 text-[10px] font-bold tracking-widest uppercase bg-[#1E3A8A] text-white rounded">Renouvellement</span>
            <RefreshCw className="h-7 w-7 text-[#1E3A8A] mb-4" />
            <div className="text-4xl font-extrabold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>360<span className="text-2xl">€</span></div>
            <p className="mt-2 text-sm text-[#475569]">Pour les joueurs du club qui renouvellent leur licence pour la saison suivante.</p>
            <ul className="mt-5 space-y-2 text-sm text-[#0F172A]">
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[#D97706] shrink-0 mt-1" /> Licence FFF saison complète</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[#D97706] shrink-0 mt-1" /> Pack équipement fourni à l'inscription (si paiement total)</li>
            </ul>
          </div>

          <div className="relative rounded-lg border border-[#E2E8F0] bg-white p-6 lg:p-8" data-testid="tarif-nouveau">
            <span className="absolute -top-3 left-6 px-2.5 py-1 text-[10px] font-bold tracking-widest uppercase bg-[#D97706] text-white rounded">Nouveau joueur</span>
            <UserPlus className="h-7 w-7 text-[#D97706] mb-4" />
            <div className="text-4xl font-extrabold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>360<span className="text-2xl">€</span></div>
            <p className="mt-2 text-sm text-[#475569]">Pour les joueurs venant d'un autre club.</p>
            <div className="mt-4 p-3 bg-[#FEF3C7] border border-[#D97706]/30 rounded-md text-xs text-[#92400E]">
              <div className="font-bold">+ Frais de mutation</div>
              <div className="mt-1">À partir de la catégorie U10. Montant susceptible d'augmenter après le 15 juillet.</div>
            </div>
            <ul className="mt-4 space-y-2 text-sm text-[#0F172A]">
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[#D97706] shrink-0 mt-1" /> Licence FFF saison complète</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[#D97706] shrink-0 mt-1" /> Pack équipement fourni à l'inscription (si paiement total)</li>
            </ul>
          </div>

          <div className="relative rounded-lg border border-[#E2E8F0] bg-white p-6 lg:p-8" data-testid="tarif-loisirs">
            <span className="absolute -top-3 left-6 px-2.5 py-1 text-[10px] font-bold tracking-widest uppercase bg-[#DC2626] text-white rounded">Loisirs</span>
            <Users className="h-7 w-7 text-[#DC2626] mb-4" />
            <div className="text-4xl font-extrabold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>250<span className="text-2xl">€</span></div>
            <p className="mt-2 text-sm text-[#475569]">Pour la section foot loisir, football à 8, sans compétition officielle.</p>
            <ul className="mt-5 space-y-2 text-sm text-[#0F172A]">
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[#D97706] shrink-0 mt-1" /> Entraînements + matchs amicaux</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[#D97706] shrink-0 mt-1" /> Pack équipement fourni à l'inscription (si paiement total)</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[#D97706] shrink-0 mt-1" /> Esprit convivial</li>
            </ul>
          </div>
        </div>

        <div className="mt-6 grid md:grid-cols-2 gap-4">
          <div className="p-5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg flex items-start gap-3" data-testid="tarif-famille-note">
            <Info className="h-5 w-5 text-[#1E3A8A] shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>Tarifs famille</div>
              <p className="text-sm text-[#475569]">Des tarifs dégressifs existent à partir de 3 enfants d'une même famille. Rapprochez-vous directement du secrétariat pour en bénéficier.</p>
            </div>
          </div>
          <div className="p-5 bg-[#FEF3C7]/60 border border-[#D97706]/30 rounded-lg flex items-start gap-3" data-testid="charge-famille-note">
            <AlertCircle className="h-5 w-5 text-[#92400E] shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-[#92400E]" style={{ fontFamily: "Outfit" }}>À la charge des familles</div>
              <p className="text-sm text-[#92400E]/90"><span className="font-semibold">Les crampons et les protège-tibias</span> ne sont pas fournis par le club et restent à l'achat par la famille.</p>
            </div>
          </div>
        </div>
      </section>

      {/* DEUX COLONNES: NOUVELLE INSCRIPTION / RENOUVELLEMENT */}
      <section className="bg-[#F8FAFC] border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="mb-10">
            <span className="text-xs tracking-[0.3em] uppercase text-[#1E3A8A] font-bold">Comment ça marche</span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: "Outfit" }}>
              Nouvelle inscription ou renouvellement ?
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Nouvelle inscription */}
            <div className="rounded-lg border border-[#E2E8F0] bg-white p-6 lg:p-8" data-testid="nouvelle-inscription">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-md bg-[#D97706]/10 flex items-center justify-center">
                  <UserPlus className="h-5 w-5 text-[#D97706]" />
                </div>
                <h3 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>Nouvelle inscription</h3>
              </div>
              <p className="text-sm text-[#475569]">Votre enfant n'a jamais été licencié au Villeurbanne United FC.</p>

              <div className="mt-6">
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#D97706] mb-3">Documents à fournir</div>
                <ul className="space-y-2.5 text-sm">
                  <li className="flex items-start gap-2.5" data-testid="doc-cni">
                    <CheckCircle2 size={16} className="text-[#1E3A8A] shrink-0 mt-0.5" />
                    <span><span className="font-semibold">Carte nationale d'identité</span> de l'enfant (pour les nationalités françaises)</span>
                  </li>
                  <li className="flex items-start gap-2.5" data-testid="doc-photo">
                    <CheckCircle2 size={16} className="text-[#1E3A8A] shrink-0 mt-0.5" />
                    <span><span className="font-semibold">Une photo d'identité</span> récente</span>
                  </li>
                  <li className="flex items-start gap-2.5 p-3 bg-[#FEF3C7] border border-[#D97706]/30 rounded-md" data-testid="doc-etranger">
                    <AlertCircle size={16} className="text-[#92400E] shrink-0 mt-0.5" />
                    <span className="text-[#92400E]">
                      <span className="font-bold">Enfants de nationalité étrangère :</span> merci de vous rapprocher directement du secrétariat du club. Les pièces à fournir diffèrent selon la situation.
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Renouvellement */}
            <div className="rounded-lg border border-[#1E3A8A] bg-white p-6 lg:p-8" data-testid="renouvellement">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-md bg-[#1E3A8A]/10 flex items-center justify-center">
                  <RefreshCw className="h-5 w-5 text-[#1E3A8A]" />
                </div>
                <h3 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>Renouvellement</h3>
              </div>
              <p className="text-sm text-[#475569]">Votre enfant était licencié la saison passée au Villeurbanne United FC.</p>

              <div className="mt-6">
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#1E3A8A] mb-3">Documents à fournir</div>
                <ul className="space-y-2.5 text-sm">
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 size={16} className="text-[#1E3A8A] shrink-0 mt-0.5" />
                    <span><span className="font-semibold">Dossier de renouvellement</span> complété (remis par le secrétariat ou le coach)</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 size={16} className="text-[#1E3A8A] shrink-0 mt-0.5" />
                    <span><span className="font-semibold">Une photo d'identité</span> récente (si celle en dossier a plus de 3 ans)</span>
                  </li>
                </ul>
                <p className="mt-4 text-xs text-[#475569] italic">
                  Pas besoin de refournir les pièces d'identité déjà en dossier. Pour toute question, contactez le secrétariat.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ÉTAPES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="mb-10">
          <span className="text-xs tracking-[0.3em] uppercase text-[#1E3A8A] font-bold">Mode d'emploi</span>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: "Outfit" }}>
            5 étapes simples
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="etapes">
          {[
            { n: 1, title: "Pré-inscription en ligne", desc: "Remplissez le formulaire ci-dessous. Vos coordonnées sont transmises au président.", icon: UserPlus },
            { n: 2, title: "Séance d'essai", desc: "Vous êtes recontacté pour une séance d'essai (détection) avec le coach de la catégorie.", icon: Target },
            { n: 3, title: "Remise du dossier", desc: "Le dossier d'inscription vous est remis à la fin de la séance d'essai, en main propre.", icon: FileText },
            { n: 4, title: "Dépôt du dossier", desc: "Vous complétez, signez, puis déposez le dossier + pièces + règlement au secrétariat.", icon: MapPin },
            { n: 5, title: "Validation", desc: "Le secrétariat confirme la validation de la licence FFF par email.", icon: CheckCircle2 },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.n} className="relative rounded-lg border border-[#E2E8F0] bg-white p-6 hover:border-[#1E3A8A] hover:-translate-y-1 transition-all duration-200" data-testid={`etape-${s.n}`}>
                <div className="absolute -top-3 -left-3 w-10 h-10 rounded-full bg-[#1E3A8A] text-white flex items-center justify-center text-sm font-bold" style={{ fontFamily: "Outfit" }}>
                  {s.n}
                </div>
                <Icon className="h-6 w-6 text-[#D97706] mb-4 ml-4" />
                <div className="font-bold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>{s.title}</div>
                <p className="mt-2 text-sm text-[#475569]">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* FORMULAIRE PRÉ-INSCRIPTION */}
      <section className="bg-gradient-to-b from-[#F8FAFC] to-white border-y border-[#E2E8F0]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="mb-8 text-center">
            <span className="text-xs tracking-[0.3em] uppercase text-[#D97706] font-bold">Étape 1</span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: "Outfit" }}>
              Pré-inscription en ligne
            </h2>
            <p className="mt-3 text-[#475569] max-w-2xl mx-auto">
              Remplissez le formulaire ci-dessous. Le président sera informé immédiatement et vous recontactera
              pour vous proposer une <strong>séance d'essai</strong>. Le dossier d'inscription complet vous sera remis à la fin de cette séance.
            </p>
          </div>
          <PreInscriptionForm />
        </div>
      </section>

      {/* OÙ DÉPOSER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 lg:pb-20">
        <div className="rounded-lg border border-[#E2E8F0] bg-white overflow-hidden grid lg:grid-cols-2" data-testid="depot-section">
          <div className="p-6 lg:p-10 bg-[#1E3A8A] text-white">
            <span className="text-xs tracking-[0.3em] uppercase text-[#D97706] font-bold">Où déposer votre dossier</span>
            <h3 className="mt-3 text-2xl lg:text-3xl font-bold" style={{ fontFamily: "Outfit" }}>Stade de la Viabert</h3>
            <div className="mt-6 space-y-3 text-sm">
              <div className="flex items-start gap-2.5">
                <MapPin size={18} className="text-[#D97706] shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold">171 rue Anatole France</div>
                  <div className="text-slate-300">69100 Villeurbanne</div>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <Clock size={18} className="text-[#D97706] shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold">Horaires d'ouverture</div>
                  <div className="text-slate-300 italic">À venir — communiqués prochainement</div>
                </div>
              </div>
            </div>
          </div>
          <div className="p-6 lg:p-10">
            <span className="text-xs tracking-[0.3em] uppercase text-[#1E3A8A] font-bold">Référent inscriptions</span>
            <h3 className="mt-3 text-xl font-bold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>Besoin d'informations ?</h3>
            <p className="mt-2 text-sm text-[#475569]">Pour toute question avant de déposer votre dossier, contactez directement le président :</p>
            <div className="mt-5 space-y-3">
              <a href="mailto:presidencevufc@yahoo.com" className="flex items-center gap-3 p-3 rounded-md border border-[#E2E8F0] hover:border-[#1E3A8A] hover:bg-slate-50 transition-colors group" data-testid="contact-email">
                <div className="w-10 h-10 rounded-md bg-[#1E3A8A]/10 flex items-center justify-center group-hover:bg-[#1E3A8A] group-hover:text-white transition-colors">
                  <Mail className="h-4 w-4 text-[#1E3A8A] group-hover:text-white" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs text-[#475569]">Email</div>
                  <div className="font-semibold text-[#0F172A] group-hover:text-[#1E3A8A] break-all">presidencevufc@yahoo.com</div>
                </div>
              </a>
              <a href="tel:0632475174" className="flex items-center gap-3 p-3 rounded-md border border-[#E2E8F0] hover:border-[#1E3A8A] hover:bg-slate-50 transition-colors group" data-testid="contact-phone">
                <div className="w-10 h-10 rounded-md bg-[#1E3A8A]/10 flex items-center justify-center group-hover:bg-[#1E3A8A] group-hover:text-white transition-colors">
                  <Phone className="h-4 w-4 text-[#1E3A8A] group-hover:text-white" />
                </div>
                <div>
                  <div className="text-xs text-[#475569]">Téléphone</div>
                  <div className="font-semibold text-[#0F172A] group-hover:text-[#1E3A8A]">06 32 47 51 74</div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* DOSSIER D'INSCRIPTION — remis à la séance d'essai */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="mb-8">
          <span className="text-xs tracking-[0.3em] uppercase text-[#D97706] font-bold">Dossier d'inscription</span>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: "Outfit" }}>
            Comment récupérer le dossier ?
          </h2>
        </div>

        <div className="rounded-lg border-2 border-[#D97706] bg-gradient-to-br from-[#FEF3C7]/50 to-white p-6 lg:p-8 flex flex-col lg:flex-row items-start gap-6" data-testid="dossier-remise">
          <div className="shrink-0 w-14 h-14 rounded-md bg-[#D97706] text-white flex items-center justify-center">
            <Target className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <div className="font-bold text-[#92400E] text-lg" style={{ fontFamily: "Outfit" }}>
              Le dossier est remis en main propre à la fin de la séance d'essai (détection)
            </div>
            <p className="mt-2 text-sm text-[#78350F] leading-relaxed">
              Pour garantir un bon accueil et vérifier que la catégorie proposée correspond au niveau du joueur, le dossier
              <strong> n'est pas téléchargeable en ligne</strong>. Il vous sera remis par le coach ou le secrétariat
              <strong> à l'issue de la séance d'essai</strong> organisée après votre pré-inscription.
            </p>
            <ul className="mt-4 space-y-1.5 text-sm text-[#78350F]">
              <li className="flex items-start gap-2"><CheckCircle2 size={15} className="text-[#D97706] shrink-0 mt-0.5" /> Vous repartez avec le dossier papier complet</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={15} className="text-[#D97706] shrink-0 mt-0.5" /> Un référent vous explique les pièces à fournir</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={15} className="text-[#D97706] shrink-0 mt-0.5" /> Vous le rapportez complété au secrétariat avec le règlement</li>
            </ul>
            <a
              href="#preinscription-form"
              data-testid="cta-preinscription-dossier"
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 bg-[#D97706] hover:bg-[#B45309] text-white font-semibold rounded-md text-sm transition-colors"
            >
              Faire la pré-inscription en ligne <ArrowRight size={14} />
            </a>
          </div>
        </div>

        {docs.length > 0 && (
          <div className="mt-10">
            <div className="text-xs tracking-[0.2em] uppercase text-[#1E3A8A] font-bold mb-4">Documents utiles</div>
            <div className="grid sm:grid-cols-2 gap-3" data-testid="inscription-docs">
              {docs.map((d) => (
                <a
                  key={d.id}
                  href={d.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`inscription-doc-${d.id}`}
                  className="group flex items-start gap-4 p-5 border border-[#E2E8F0] rounded-lg bg-white hover:border-[#D97706] hover:-translate-y-0.5 transition-all duration-200"
                >
                  <div className="shrink-0 w-11 h-11 rounded-md bg-[#D97706]/10 flex items-center justify-center text-[#D97706] group-hover:bg-[#D97706] group-hover:text-white transition-colors">
                    <FileText size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[#0F172A] group-hover:text-[#D97706] transition-colors" style={{ fontFamily: "Outfit" }}>{d.title}</div>
                    {d.description && <div className="text-xs text-[#475569] mt-1">{d.description}</div>}
                  </div>
                  <Download size={16} className="text-[#CBD5E1] group-hover:text-[#D97706] shrink-0 mt-1" />
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10 p-6 bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] rounded-lg text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-[#D97706] font-bold">Une question générale ?</div>
            <p className="mt-1 text-slate-100">Consultez la FAQ complète du club pour trouver rapidement une réponse.</p>
          </div>
          <Link to="/faq" data-testid="cta-faq-inscriptions" className="px-5 py-2.5 rounded-md bg-white text-[#1E3A8A] font-semibold hover:bg-[#D97706] hover:text-white transition-colors flex items-center gap-2">
            Ouvrir la FAQ <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
